/**
 * What a name's figures say beyond its rank: when its babies were born, how the balance of girls
 * and boys has moved, how the last few years have gone, and how common it is once every spelling
 * is counted. Pure functions, shared by the app and the website's name pages.
 */
import { share } from "./format";
import type { InsightsData, Series, SexIdx } from "./types";

/** [year, babies] for every year a series covers, zero-trimmed at both ends */
export function yearly(s: Series | undefined): [number, number][] {
  if (!s || s.length < 2) return [];
  return s.slice(1).map((v, i) => [s[0] + i, v]);
}

function valueIn(s: Series | undefined, year: number): number {
  if (!s || s.length < 2) return 0;
  const i = year - s[0] + 1;
  return i >= 1 && i < s.length ? s[i] : 0;
}

// ------------------------------------------------------------------ its generation

export interface Generation {
  /** a quarter, half and three quarters of all the babies on record had been born by these years */
  from: number;
  median: number;
  to: number;
  total: number;
  peakYear: number;
  /** already well used when the records begin, so earlier babies aren't counted */
  before: boolean;
}

/** The middle half of a name's babies. `start` is the first year of the records. */
export function generation(s: Series | undefined, start: number): Generation | null {
  const pts = yearly(s);
  const total = pts.reduce((a, [, v]) => a + v, 0);
  if (total < 50 || pts.length < 3) return null;
  let run = 0;
  let from = 0;
  let median = 0;
  let to = 0;
  let peak = pts[0];
  for (const p of pts) {
    run += p[1];
    if (!from && run >= total / 4) from = p[0];
    if (!median && run >= total / 2) median = p[0];
    if (!to && run >= (total * 3) / 4) to = p[0];
    if (p[1] > peak[1]) peak = p;
  }
  const before = pts[0][0] <= start && pts[0][1] >= peak[1] / 4;
  return { from, median, to, total, peakYear: peak[0], before };
}

// ------------------------------------------------------------------ girls and boys

export interface Balance {
  y0: number;
  /** girls' share each year (0 to 1), or null where too few babies were given it to say */
  shares: (number | null)[];
  /** girls' share in the first and last years with enough babies to say */
  first: number;
  last: number;
  firstYear: number;
  lastYear: number;
  /** how far girls' share has moved: the last three of those years against the first three,
   * averaged so that one odd year isn't taken for a change */
  drift: number;
  /** the year it became mostly a girls' (or boys') name and stayed so, if it changed over */
  flip: { year: number; to: "girls" | "boys" } | null;
}

/**
 * The balance of girls and boys given a name, year by year. Null for names given almost only to
 * one sex throughout: there's nothing to show.
 */
export function balance(F: Series | undefined, M: Series | undefined, minPerYear = 10): Balance | null {
  const starts = [F, M].filter((s): s is Series => !!s && s.length > 1).map((s) => s[0]);
  if (!starts.length) return null;
  const ends = [F, M].filter((s): s is Series => !!s && s.length > 1).map((s) => s[0] + s.length - 2);
  const y0 = Math.min(...starts);
  const y1 = Math.max(...ends);
  const shares: (number | null)[] = [];
  for (let y = y0; y <= y1; y++) {
    const f = valueIn(F, y);
    const m = valueIn(M, y);
    shares.push(f + m >= minPerYear ? f / (f + m) : null);
  }
  const ok = shares.map((v, i) => [i, v] as const).filter((p): p is readonly [number, number] => p[1] !== null);
  if (ok.length < 3) return null;
  const mean = (xs: readonly (readonly [number, number])[]) => xs.reduce((a, [, v]) => a + v, 0) / xs.length;
  const early = mean(ok.slice(0, 3));
  const late = mean(ok.slice(-3));
  const lo = Math.min(...ok.map(([, v]) => v));
  const hi = Math.max(...ok.map(([, v]) => v));
  // both sexes matter now, or the balance has moved a long way
  if (!(late >= 0.1 && late <= 0.9) && hi - lo < 0.25) return null;
  // the run of years, back from the latest, with more of one than the other (a tie ends it),
  // after years with more of the other
  let flip: Balance["flip"] = null;
  const now = side(ok[ok.length - 1][1]);
  let j = ok.length - 1;
  if (now) while (j > 0 && side(ok[j - 1][1]) === now) j--;
  if (now && ok.length - j >= 3 && ok.slice(0, j).some(([, v]) => side(v) === other(now))) flip = { year: y0 + ok[j][0], to: now };
  // the years quoted are ones listing both girls and boys: the records leave out a name given to
  // very few babies of a sex, so 0% only means one of the two was too rare to be listed
  const both = ok.filter(([, v]) => v > 0 && v < 1);
  if (!both.length) return null;
  const [q0, q1] = [both[0], both[both.length - 1]];
  return { y0, shares, first: q0[1], last: q1[1], firstYear: y0 + q0[0], lastYear: y0 + q1[0], drift: late - early, flip };
}

/** which there were more of, given girls' share; null for a tie */
const side = (girls: number) => (girls > 0.5 ? "girls" : girls < 0.5 ? "boys" : null);
const other = (s: "girls" | "boys") => (s === "girls" ? "boys" : "girls");

/** a share for a sentence: "just over half" where "50%" would hide which there were more of */
const shareWords = (x: number) => (x > 0.5 && x < 0.505 ? "just over half" : x < 0.5 && x > 0.495 ? "just under half" : share(x));

/**
 * The balance in a sentence, quoting single years so the figures can be checked against the
 * chart: "In 1996, 74% of the babies named Cody were girls. By 2025 it was under 1%, with more
 * boys than girls since 1999." `where` follows the name (" in the UK").
 */
export function balanceWords(b: Balance, name: string, where = ""): string {
  const named = `the babies named ${name}${where}`;
  if (b.flip && side(b.first) === other(b.flip.to)) {
    const then = `In ${b.firstYear}, ${shareWords(b.first)} of ${named} were girls.`;
    const more = `more ${b.flip.to} than ${other(b.flip.to)} since ${b.flip.year}`;
    // the last year listing both may come before the change, once the other became too rare to list
    return b.lastYear >= b.flip.year
      ? `${then} By ${b.lastYear} it was ${shareWords(b.last)}, with ${more}.`
      : `${then} It has been given to ${more}.`;
  }
  if (Math.abs(b.drift) >= 0.15 && Math.abs(b.last - b.first) >= 0.1)
    return `Girls were ${shareWords(b.first)} of ${named} in ${b.firstYear}, and ${shareWords(b.last)} in ${b.lastYear}.`;
  return `In ${b.lastYear}, ${shareWords(b.last)} of ${named} were girls.`;
}

/**
 * A Trends "changing sides" entry's girls' share (in tenths of a per cent, -1 for too few) in
 * the first and last years listing both girls and boys, as `balance` quotes them.
 */
export function crossEnds(x: { y: number; s: number[] }): { first: number; firstYear: number; last: number; lastYear: number } {
  const ends = (test: (v: number) => boolean) => [x.s.findIndex(test), x.s.length - 1 - [...x.s].reverse().findIndex(test)];
  let [i, j] = ends((v) => v > 0 && v < 1000);
  if (i < 0) [i, j] = ends((v) => v >= 0);
  return { first: x.s[i] / 1000, firstYear: x.y + i, last: x.s[j] / 1000, lastYear: x.y + j };
}

// ------------------------------------------------------------------ lately

export interface Run {
  dir: "up" | "down";
  /** years in a row it has risen (or fallen), up to the latest */
  years: number;
}

/** Whether a name has risen (or fallen) several years in a row, up to `latest`. */
export function lately(s: Series | undefined, latest: number): Run | null {
  const pts = yearly(s);
  if (pts.length < 4 || pts[pts.length - 1][0] !== latest) return null;
  const n = pts.length;
  const dir = pts[n - 1][1] > pts[n - 2][1] ? "up" : pts[n - 1][1] < pts[n - 2][1] ? "down" : null;
  if (!dir) return null;
  let years = 0;
  for (let i = n - 1; i > 0; i--) {
    const d = pts[i][1] - pts[i - 1][1];
    if ((dir === "up" && d > 0) || (dir === "down" && d < 0)) years++;
    else break;
  }
  return years >= 3 ? { dir, years } : null;
}

export interface Landmark {
  kind: "most" | "fewest";
  /** the latest year beats every year since this one; null: every year on record */
  since: number | null;
}

/**
 * The latest year in context: the most babies in any year since 2009 (or ever), or the fewest.
 * Only said when the comparison reaches back at least `minGap` years.
 */
export function landmark(s: Series | undefined, latest: number, minGap = 5): Landmark | null {
  const pts = yearly(s);
  if (pts.length < minGap + 1 || pts[pts.length - 1][0] !== latest) return null;
  const v = pts[pts.length - 1][1];
  const earlier = pts.slice(0, -1);
  for (const kind of ["most", "fewest"] as const) {
    const beats = (x: number) => (kind === "most" ? v > x : v < x);
    let i = earlier.length - 1;
    while (i >= 0 && beats(earlier[i][1])) i--;
    if (i < 0) return { kind, since: null };
    if (latest - earlier[i][0] >= minGap) return { kind, since: earlier[i][0] };
  }
  return null;
}

// ------------------------------------------------------------------ every spelling counted

export interface Family {
  /** babies given any of its spellings, and that total's rank among names counted this way */
  total: number;
  rank: number;
  /** spellings, most common first */
  members: { key: string; count: number }[];
}

const familyMaps = new WeakMap<InsightsData, Map<string, Family>[]>();

/** The family of spellings a name belongs to, for girls (0) or boys (1), if it has others. */
export function familyOf(ins: InsightsData, key: string, si: SexIdx): Family | null {
  let maps = familyMaps.get(ins);
  if (!maps) {
    maps = (["F", "M"] as const).map((sx) => {
      const m = new Map<string, Family>();
      for (const row of ins.families[sx] ?? []) {
        const members: Family["members"] = [];
        for (let i = 2; i + 1 < row.length; i += 2) members.push({ key: String(row[i]), count: Number(row[i + 1]) });
        const fam = { total: Number(row[0]), rank: Number(row[1]), members };
        for (const mem of members) m.set(mem.key, fam);
      }
      return m;
    });
    familyMaps.set(ins, maps);
  }
  return maps[si].get(key) ?? null;
}
